/**
 * Presses on the view cube, shared by both platforms: a press goes down on a
 * region or button of the `viewCubeLayout`, turns the view if it travels
 * (`moveViewCubeDrag`), and on release takes its region or button if it
 * stayed a tap (`endViewCubeDrag`), which `tapViewCube` carries out.
 */
import type { Point } from '@kaiizen/kai-floor-plan';
import { VIEW_ELEVATION_LIMIT } from './camera3d';
import { type CameraAngle, TAP_MAX_DISTANCE_PX, type TurnDirection } from './View3DCore';
import { isViewCubeButton, viewCubeAngle, type ViewCubeHit, type ViewCubeLayout, viewCubeRegionAt } from './viewCube';

/** A press on the view cube, from down to up. */
export interface ViewCubeDrag {
  /** Where it went down, in the cube area's coordinates. */
  start: Point;
  /** The camera's angle then. */
  startAngle: CameraAngle;
  /** What a tap there takes. */
  hit: ViewCubeHit;
  /** How far the view turns per point dragged: half the cube's side turns it a quarter turn, as far as the cube's surface moves. */
  radiansPerPoint: number;
  /** Whether it has traveled past `TAP_MAX_DISTANCE_PX`: a drag, which turns the view and never picks. */
  dragging: boolean;
  /** The angle the drag turns the view to (for `View3DCore.setCameraAngle`); null while it is still a tap, and always on a button. */
  angle: CameraAngle | null;
}

/** A press going down at `point` with the camera at `angle`; null off the cube and its buttons, a press for the scene. */
export function beginViewCubeDrag(layout: ViewCubeLayout, angle: CameraAngle, point: Point): ViewCubeDrag | null {
  const hit = viewCubeRegionAt(layout, point);
  return hit && { start: { ...point }, startAngle: { ...angle }, hit, radiansPerPoint: Math.PI / layout.side, dragging: false, angle: null };
}

/**
 * The press moved to `point`. Once it has traveled past
 * `TAP_MAX_DISTANCE_PX` it is a drag for good, and turns the view as if the
 * cube were grabbed where it went down: dragging right turns the cube, and
 * the house, to the right (the camera orbits left), and dragging down tips
 * its top toward the viewer, within the view cube's Top and Bottom.
 */
export function moveViewCubeDrag(drag: ViewCubeDrag, point: Point): ViewCubeDrag {
  const dragging = drag.dragging || Math.hypot(point.x - drag.start.x, point.y - drag.start.y) > TAP_MAX_DISTANCE_PX;
  if (!dragging || isViewCubeButton(drag.hit)) return { ...drag, dragging };
  const azimuth = drag.startAngle.azimuth - (point.x - drag.start.x) * drag.radiansPerPoint;
  const elevation = Math.min(
    VIEW_ELEVATION_LIMIT,
    Math.max(-VIEW_ELEVATION_LIMIT, drag.startAngle.elevation + (point.y - drag.start.y) * drag.radiansPerPoint),
  );
  return { ...drag, dragging, angle: { azimuth, elevation } };
}

/** What the press takes on release (hand it to `tapViewCube`): its region or button if it stayed a tap; null after a drag. */
export const endViewCubeDrag = (drag: ViewCubeDrag): ViewCubeHit | null => drag.dragging ? null : drag.hit;

/** What a tap on the view cube asks of the view: `View3DCore` has all three, and so may a host's own view controls. */
export interface ViewCubeControls {
  viewFrom(angle: CameraAngle): void;
  goHome(): void;
  turnView(direction: TurnDirection): void;
}

/** Do what a tap on `hit` asks, the same on every platform: look from a region, glide home, or spin a quarter turn; nothing without a tap. */
export function tapViewCube(hit: ViewCubeHit | null, controls: ViewCubeControls): void {
  if (hit === null) return;
  if (hit === 'home') controls.goHome();
  else if (hit === 'turn-left') controls.turnView('left');
  else if (hit === 'turn-right') controls.turnView('right');
  else controls.viewFrom(viewCubeAngle(hit));
}
